import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Button, Form, Input, DatePicker, Select, message, Spin, Image, Upload } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../../../utils/api';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import ImgCrop from 'antd-img-crop';

export default function UserInfo() {
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  
  // Custom upload handler
  const customRequest = async ({ file, onSuccess, onError, title, uploadMark, fieldName }) => {
    const formData = new FormData();
    formData.append('files', file);
    
    try {
      const res = await api.post('/uploads/odm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { title, uploadMark }
      });
      
      if (res && res.code === 200 && res.data && res.data.length > 0) {
        const url = res.data[0].url;
        onSuccess(url);
        form.setFieldsValue({ [fieldName]: url });
        if (fieldName === 'avatarUrl') {
          setAvatarUrl(url);
        } else if (fieldName === 'qrUrl') {
          setQrUrl(url);
        }
      } else {
        onError(new Error('Upload failed'));
      }
    } catch (e) {
      console.error(e);
      onError(e);
    }
  };

  useEffect(() => {
    if (user) {
       form.setFieldsValue({
         ...user,
         birthDate: user.birthDate ? dayjs(user.birthDate) : null
       });
       setAvatarUrl(user.avatarUrl || '');
       setQrUrl(user.qrUrl || '');
    }
  }, [user, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null
      };
      
      setLoading(true);
      const res = await api.put(`/users/${user.id}`, payload);
      if (res && res.code === 200) {
        message.success('保存成功');
        setIsEditing(false);
        // Update context/localStorage
        // But PUT returns User object usually, check ApiResponse
        // UsersController update returns ApiResponse<User>
        if (res.data) {
             updateUser(res.data);
        }
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      console.error(e);
      // Form validation error or API error
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <Spin />;

  return (
    <div className="profile-user-info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3>个人信息</h3>
        {!isEditing ? (
          <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)}>编辑</Button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<CloseOutlined />} onClick={() => setIsEditing(false)}>取消</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={loading}>保存</Button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item>
             {user?.avatarUrl ? <Image width={50} src={user.avatarUrl} /> : '暂无'}
          </Descriptions.Item>
          <Descriptions.Item>
             {user?.qrUrl ? <Image width={50} src={user.qrUrl} /> : '暂无'}
          </Descriptions.Item>
          <Descriptions.Item label="用户名">{user?.username}</Descriptions.Item>
          <Descriptions.Item label="真实姓名">{user?.realName || '-'}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{user?.email}</Descriptions.Item>
          <Descriptions.Item label="手机">{user?.mobile}</Descriptions.Item>
          <Descriptions.Item label="性别">{user?.gender === 'M' ? '男' : user?.gender === 'F' ? '女' : '-'}</Descriptions.Item>
          <Descriptions.Item label="生日">{user?.birthDate ? dayjs(user.birthDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Form form={form} layout="vertical">
          <div style={{display: 'flex', justifyContent: 'space-around'}}>
            <Form.Item label="头像" name="avatarUrl">
              <ImgCrop rotationSlider>
                <Upload
                  customRequest={(options) => customRequest({ ...options, title: user.username + '_Avatar', uploadMark: 'userProfile', fieldName: 'avatarUrl' })}
                  listType="picture-card"
                  showUploadList={false}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" style={{ width: '100%' }} />
                  ) : (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传头像</div>
                    </div>
                  )}
                </Upload>
              </ImgCrop>
            </Form.Item>
            <Form.Item label="二维码" name="qrUrl">
              <ImgCrop rotationSlider aspect={1}>
                <Upload
                  customRequest={(options) => customRequest({ ...options, title: user.username + '_QrCode', uploadMark: 'userProfile', fieldName: 'qrUrl' })}
                  listType="picture-card"
                  showUploadList={false}
                >
                  {qrUrl ? (
                    <img src={qrUrl} alt="qr" style={{ width: '100%' }} />
                  ) : (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传二维码</div>
                    </div>
                  )}
                </Upload>
              </ImgCrop>
            </Form.Item>
          </div>
          <Form.Item label="邮箱" name="email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="手机" name="mobile">
            <Input />
          </Form.Item>
          <Form.Item label="性别" name="gender">
            <Select>
              <Select.Option value="M">男</Select.Option>
              <Select.Option value="F">女</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="生日" name="birthDate">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      )}
    </div>
  );
}
